// Single source of truth for the ordered changelog list used by both
// WhatsNewModal (rendering) and AppShell (auto-open + acknowledgment).
// Keeping this in one place prevents drift between the two — historically
// the mismatch caused the modal-loop bug where AppShell picked one
// "latest" version and the modal rendered a different one.
//
// Behaviour:
//  1. Merge bundled JSON (frontend fallback) with API entries (Mongo).
//  2. Dedupe by `version` — API entries win when both sides have the
//     same version so a fresh backend edit takes precedence over the
//     bundled copy.
//  3. Sort by ISO `date` descending. Falls back to comparing `version`
//     strings if a date is missing on either side.
//
// Result: `entries[0]` is always the true latest release, regardless of
// whether the API happens to be temporarily stale, out-of-order, or
// unreachable.
import bundledChangelog from "./changelog.json";

export function mergeAndSortChangelog(apiEntries) {
  const api = Array.isArray(apiEntries) ? apiEntries : [];
  const byVersion = new Map();
  // Bundled first so API entries can overwrite on the same version key.
  for (const entry of bundledChangelog) {
    if (entry && entry.version) byVersion.set(entry.version, entry);
  }
  for (const entry of api) {
    if (entry && entry.version) byVersion.set(entry.version, entry);
  }
  const merged = Array.from(byVersion.values());
  merged.sort((a, b) => {
    const ad = a.date || "";
    const bd = b.date || "";
    if (ad !== bd) return bd.localeCompare(ad);
    return String(b.version || "").localeCompare(String(a.version || ""), undefined, { numeric: true });
  });
  return merged;
}
